<template>
	<div class="p-p-2 content">
		<BatchClassList :batchClasses="batchClasses" :loading="loading" @onClickDelete="openDeleteDialog" @onEntrySelect="onEntrySelect" @openTranslations="openTranslations" />
	</div>
	<DialogDelete :showDialog="deleteDialog" @onClose="deleteDialog = false" @onConfirm="deleteEntry" />
</template>

<script lang="ts">
import {
	defineComponent, onMounted, ref
} from 'vue';
import {ClientManager} from "@/singletons/ClientManager";
import {BatchClass} from "@dex/squeeze-client-ts";
import BatchClassList from "@/apps/administration/components/batchclasses/BatchClassList.vue";
import DialogDelete from "@/components/DialogDelete.vue";
import {ToastManager} from "@/util/ToastManager";
import { useI18n } from 'vue-i18n'
import { useToast } from "primevue/usetoast";
import {useSqueezeStore} from "@/apps/squeeze/store";
import router from "@/router";

export default defineComponent({
	name: "BatchClassListView",
	components: {
		BatchClassList,DialogDelete,
	},
	setup() {
		const batchClassApi = ClientManager.getInstance().squeeze.batchClass;
		const store = useSqueezeStore();
		/** Name of the persistent batch class setting that backs the "LLM active" column */
		const LLM_EXTRACTION_SETTING = "LLMExtraction";
		const batchClasses = ref<(BatchClass & { llmActive?: boolean })[]>([]);
		const loading = ref<boolean>(false);
		const deleteDialog = ref<boolean>(false);
		const batchClass = ref<BatchClass>({name: "", description: ''});
		const {t} = useI18n();
		const toast = useToast();

		/**
		 * Lazily loads the LLMExtraction setting for each batch class to fill the "LLM active" column.
		 * Only runs when the FeatureFlag is active (one settings request per row).
		 */
		const loadLlmSettings = () => {
			if (!store.state.featureSet.llmExtraction) {
				return;
			}

			batchClasses.value.forEach(currentBatchClass => {
				if (!currentBatchClass.id) {
					return;
				}

				batchClassApi.getBatchClassSettings(currentBatchClass.id)
					.then(settings => {
						const setting = (settings || []).find(s => s.settingName === LLM_EXTRACTION_SETTING);
						currentBatchClass.llmActive = setting?.value === 'true';
					})
					.catch(() => {
						currentBatchClass.llmActive = false;
					});
			});
		}

		/** Reloads the data */
		const reloadData = () => {
			loading.value = true;
			batchClassApi.getAllBatchClasses().then(data => {
				batchClasses.value = data;
				loading.value = false;
				loadLlmSettings();
			}).catch((err) => {
				loading.value = false
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.statusText);
			});
		}

		/**
		 * Gets all Batch-Classes on Mounted
		 */
		onMounted(() =>{
			reloadData();
		});

		/** Triggered when a row is selected */
		const onEntrySelect = (batchClassId: number) => {
			router.push({ name: 'BatchClassView', params: { batchClassId: batchClassId }})
		}

		/** Opens the translations page for all batch class translation keys */
		const openTranslations = () => {
			router.push({ name: 'BatchClassTranslationsView' });
		}

		/**
		 * Opens the Delete Dialog
		 * @param row Row to delete
		 */
		const openDeleteDialog = (row: BatchClass) => {
			deleteDialog.value = true;
			batchClass.value = row;
		}

		const deleteEntry = () => {
			loading.value = true;
			if (batchClass.value.id) {
				batchClassApi.deleteBatchClassById(batchClass.value.id).then(() => {
					reloadData();
				}).catch((err) => {
					loading.value = false;
					ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.statusText);
				})
			}
		}

		return {
			batchClasses, loading, deleteDialog, openDeleteDialog, deleteEntry, reloadData, onEntrySelect, openTranslations,
		}
	},
})
</script>

<style scoped>

</style>
